import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Table, Card, Row, Col, Nav } from 'react-bootstrap';
import { FiDownload } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { getTenantFromToken } from 'utils/routeUtils';

const DynamicModal = ({ show, onClose, modalData }) => {
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const tenant = getTenantFromToken();

  // Check if modalData has tabs (now checking in extra object)
  const hasTabs =
    modalData?.extra?.tabs &&
    Array.isArray(modalData.extra.tabs) &&
    modalData.extra.tabs.length > 0;

  // Get title and subtitle from extra
  const title = modalData?.extra?.title || 'Details';
  const subtitle = modalData?.extra?.subtitle;

  // Get tabs and data from extra
  const tabs = modalData?.extra?.tabs || [];
  const data = modalData?.extra?.data || {};

  // Get modal buttons
  const topButtons = modalData?.modalButtons?.topButtons || [];

  // Function to handle button clicks
  const handleButtonClick = btn => {
    if (!btn.enabled) return;

    const buttonName = btn.buttonName.toLowerCase();
    const id = modalData?.id;

    switch (buttonName) {
      case 'Edit':
        if (btn.redirectPath && id) {
          navigate(`/${tenant}${btn.redirectPath}/${id}`);
        }
        break;
      case 'view':
        if (btn.redirectPath && id) {
          navigate(`/${tenant}${btn.redirectPath}/${id}`);
        }
        break;
      case 'delete':
        // Handle delete logic here
        console.log('Delete action for ID:', id);
        break;
      default:
        if (btn.redirectPath) {
          const path = id
            ? `/${tenant}${btn.redirectPath}/${id}`
            : `/${tenant}${btn.redirectPath}`;
          navigate(path);
        }
        break;
    }

    // Close modal after navigation (optional)
    onClose();
  };

  // Function to render card data
  const renderCardData = cardData => {
    return (
      <Row>
        {Object.entries(cardData.datas || {}).map(([key, value], index) => (
          <Col md={4} key={index} className="mb-3">
            <Card className="h-100">
              <Card.Body className="text-center">
                <Card.Title className="h5">{key}</Card.Title>
                <Card.Text className="display-6 text-primary">
                  {value}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  // Function to render table data
  const renderTableData = tableData => {
    if (!tableData.datas || tableData.datas.length === 0) {
      return <p>No data available</p>;
    }

    // Get column headers from the first data object
    const headers = Object.keys(tableData.datas[0]);

    return (
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th className="fw-semibold text-start border" key={index}>
                {header.replace(/_/g, ' ').toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.datas.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {headers.map((header, colIndex) => (
                <td className="fs--1" key={colIndex}>
                  {row[header]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  // Function to render content based on type
  const renderContent = contentData => {
    if (!contentData) return <p>No data available</p>;

    switch (contentData.type) {
      case 'card':
        return renderCardData(contentData);
      case 'table':
        return renderTableData(contentData);
      default:
        return <p>Unsupported data type</p>;
    }
  };

  // Function to handle Excel export
  const handleDownloadExcel = () => {
    if (!data) return;

    const workbook = XLSX.utils.book_new();

    // Add title sheet
    const titleData = [[title || 'Report'], [subtitle || ''], []];

    if (hasTabs) {
      // Export each tab's data as separate sheets
      tabs.forEach(tabName => {
        const tabData = data[tabName];
        if (tabData) {
          let sheetData = [];

          if (tabData.type === 'card') {
            sheetData = [
              [tabData.tittle || tabName],
              [],
              ['Metric', 'Value'],
              ...Object.entries(tabData.datas || {}).map(([key, value]) => [
                key,
                value
              ])
            ];
          } else if (tabData.type === 'table' && tabData.datas) {
            const headers = Object.keys(tabData.datas[0] || {});
            sheetData = [
              [tabData.tittle || tabName],
              [],
              headers.map(h => h.replace(/_/g, ' ').toUpperCase()),
              ...tabData.datas.map(row => headers.map(header => row[header]))
            ];
          }

          if (sheetData.length > 0) {
            const ws = XLSX.utils.aoa_to_sheet(sheetData);

            // Style the header
            if (ws['A1']) {
              ws['A1'].s = {
                alignment: { horizontal: 'center', vertical: 'center' },
                font: { bold: true, sz: 14 }
              };
            }

            // Set column widths
            const maxCols = Math.max(...sheetData.map(row => row.length));
            ws['!cols'] = Array(maxCols).fill({ wch: 20 });

            XLSX.utils.book_append_sheet(
              workbook,
              ws,
              tabName.substring(0, 31)
            );
          }
        }
      });
    } else {
      // Export single data sheet
      const ws = XLSX.utils.aoa_to_sheet(titleData);
      XLSX.utils.book_append_sheet(workbook, ws, 'Report');
    }

    const fileName = `${title?.replace(/[^a-z0-9]/gi, '_') || 'Report'}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  // Get current content to display
  const getCurrentContent = () => {
    if (!data) return null;

    if (hasTabs) {
      const currentTabName = tabs[activeTab];
      return data[currentTabName];
    } else {
      // If no tabs, show the first data item or the data directly
      const dataKeys = Object.keys(data);
      return data[dataKeys[0]];
    }
  };

  const currentContent = getCurrentContent();

  return (
    <Modal show={show} onHide={onClose} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          <div>
            <div>{title}</div>
            {subtitle && <small className="text-muted">{subtitle}</small>}
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <div className="d-flex justify-content-end align-items-center mb-3">
          <div className="d-flex">
            {/* Top Buttons */}
            {topButtons
              ?.filter(btn => btn.enabled) // Only show enabled buttons
              .map((btn, index) => (
                <Button
                  key={index}
                  variant={btn.variant || 'outline-primary'}
                  size="sm"
                  onClick={() => handleButtonClick(btn)}
                  className={index > 0 ? 'ms-2' : ''}
                >
                  {btn.buttonName}
                </Button>
              ))}
          </div>
          {/* <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleDownloadExcel}
          >
            <FiDownload className="me-1" />
            Export Excel
          </Button> */}
        </div>

        {hasTabs && (
          <Nav variant="tabs" className="mb-3">
            {tabs.map((tabName, index) => (
              <Nav.Item key={index}>
                <Nav.Link
                  active={activeTab === index}
                  onClick={() => setActiveTab(index)}
                  style={{ cursor: 'pointer' }}
                >
                  {tabName}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        )}

        <div className="tab-content">
          {currentContent && (
            <div>
              {currentContent.tittle && (
                <h5 className="mb-3">{currentContent.tittle}</h5>
              )}
              {renderContent(currentContent)}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

DynamicModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tenant: PropTypes.string,
  modalData: PropTypes.shape({
    id: PropTypes.string,
    processing_center: PropTypes.string,
    input_crop_count: PropTypes.number,
    process_stage_count: PropTypes.number,
    output_product_count: PropTypes.number,
    extra: PropTypes.shape({
      title: PropTypes.string,
      subtitle: PropTypes.string,
      tabs: PropTypes.arrayOf(PropTypes.string),
      data: PropTypes.object
    }),
    action: PropTypes.shape({
      viewData: PropTypes.bool
    }),
    modalButtons: PropTypes.shape({
      topButtons: PropTypes.arrayOf(
        PropTypes.shape({
          enabled: PropTypes.bool,
          buttonName: PropTypes.string,
          redirectPath: PropTypes.string,
          variant: PropTypes.string
        })
      )
    })
  })
};

DynamicModal.defaultProps = {
  tenant: 'default',
  modalData: {
    id: '',
    extra: {
      title: '',
      subtitle: '',
      tabs: [],
      data: {}
    },
    modalButtons: {
      topButtons: []
    }
  }
};

export default DynamicModal;
